/**
 * Map Validators — Geographic question types for TOPO module.
 *
 * Covers: map_locate_region, map_locate_point, map_label_regions.
 *
 * Answer payload conventions (per TOPO_MAP_QUESTIONS_INVESTIGATION §12.1):
 *   map_locate_region → string regionId (e.g. "NL-LI")
 *   map_locate_point  → JSON.stringify({lat, lon})
 *   map_label_regions → JSON.stringify(Record<labelId, regionId>)
 */

import type { ValidatorFn } from './shared';
import { haversineKm } from '../../../visuals/components/topoMap/geo';
import { resolveRegionName } from '../../../visuals/components/topoMap/nameResolver';

// ── map_locate_region ────────────────────────────────────────

/**
 * Compare two region identifiers tolerantly.
 *
 * Map atlases come from different sources (PDOK provinces, runtime-built
 * islands, future EU/world atlases). A click is correct when:
 *   - the raw IDs match exactly (canonical case), OR
 *   - both IDs resolve to the same human-readable name in the resolver
 *     (defensive fallback — e.g. if a polygon is exposed under "Ameland"
 *     while the seed uses "NL-AMELAND", the name lookup unifies them).
 */
function regionsMatch(selected: string, correct: string): boolean {
  if (!selected || !correct) return false;
  if (selected === correct) return true;
  const selectedName = resolveRegionName(selected).trim().toLowerCase();
  const correctName = resolveRegionName(correct).trim().toLowerCase();
  if (!selectedName || !correctName) return false;
  // Only count as match if at least one side actually resolved (otherwise
  // both are just the raw IDs and we'd be re-comparing the same strings).
  const selectedResolved = selectedName !== selected.trim().toLowerCase();
  const correctResolved = correctName !== correct.trim().toLowerCase();
  if (!selectedResolved && !correctResolved) return false;
  return selectedName === correctName;
}
export const validateMapLocateRegion: ValidatorFn = (userAnswer, question) => {
  const selected = String(userAnswer ?? '');
  const data = (question.data ?? {}) as Record<string, unknown>;
  const correct = String(data.correctRegionId ?? '');
  const isCorrect = regionsMatch(selected, correct);
  // NOTE: do NOT set displayAnswer — PracticeSession overwrites the raw
  // userTypedAnswer with displayAnswer. Setting it to `correct` would replace
  // the user's actual click with the correct ID and break the wrong-answer
  // comparison. formatUserAnswer in feedbackAnswerFormatters.ts resolves the
  // selected ID to a human-readable name from the raw payload.
  return {
    isCorrect,
    distractorIndex: undefined,
    correctAnswerDisplay: correct
  };
};

// ── map_locate_point ─────────────────────────────────────────

interface PointPayload {
  lat: number;
  lon: number;
}
function tryParsePoint(raw: string): PointPayload | null {
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && typeof parsed.lat === 'number' && typeof parsed.lon === 'number') {
      return {
        lat: parsed.lat,
        lon: parsed.lon
      };
    }
  } catch {

    /* fallthrough */}
  return null;
}
export const validateMapLocatePoint: ValidatorFn = (userAnswer, question) => {
  const data = (question.data ?? {}) as Record<string, unknown>;
  const mode = data.mode === 'snap-to-dot' ? 'snap-to-dot' : 'free';

  // Backward compatibility: if correctCityId is present but targetPoint is not, treat as snap-to-dot
  const isSnapToDot = mode === 'snap-to-dot' || data.correctCityId && !data.targetPoint;
  if (isSnapToDot) {
    const selected = String(userAnswer ?? '');
    const correct = String(data.correctCityId ?? '');
    const isCorrect = selected.length > 0 && selected === correct;
    return {
      isCorrect,
      distractorIndex: undefined,
      correctAnswerDisplay: correct
    };
  }

  // Free mode (default)
  const click = tryParsePoint(String(userAnswer ?? ''));
  const target = data.targetPoint as {
    lat: number;
    lon: number;
    label?: string;
  } | undefined;
  const tolKm = typeof data.toleranceKm === 'number' ? data.toleranceKm : 30;
  if (!click || !target) {
    return {
      isCorrect: false
    };
  }
  const dist = haversineKm([click.lon, click.lat], [target.lon, target.lat]);
  const isCorrect = dist <= tolKm;
  // NOTE: do NOT set displayAnswer here — PracticeSession overwrites the raw
  // userTypedAnswer with displayAnswer, and the feedback layer (formatUserAnswer
  // + MapLocatePointBreakdown) needs the raw JSON `{lat,lon}` payload to compute
  // distance-off and resolve the nearest city. Display formatting happens in
  // feedbackAnswerFormatters.ts from the raw payload.
  return {
    isCorrect,
    correctAnswerDisplay: target.label
  };
};

// ── map_locate_line ──────────────────────────────────────────
// Kid clicks at a river/waterway on the map. Validator uses shortest
// distance from the click to the target river polyline (point-to-segment
// haversine). Correct when distance ≤ toleranceKm.

interface RiverAtlas {
  rivers: Array<{
    id: string;
    name: string;
    path: [number, number][]; // [lon, lat]
  }>;
}

// Lazy-loaded so the validator does not pull the atlas JSON into bundles
// that never use it. The runtime import is cheap in practice (single small JSON).
let _riversCache: RiverAtlas | null = null;
function loadRivers(): RiverAtlas {
  if (_riversCache) return _riversCache;
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const data = require('../../atlases/nl/rivers.json') as RiverAtlas;
  _riversCache = data;
  return data;
}

/**
 * Shortest distance (km) from a point to a polyline, using haversine on
 * point-to-segment geometry. Treats each segment as a great-circle approximation
 * which is fine for the small Dutch scale (<400 km extent).
 */
function pointToPolylineKm(click: {
  lat: number;
  lon: number;
}, path: [number, number][]): number {
  if (path.length < 2) return Infinity;
  let best = Infinity;
  for (let i = 0; i < path.length - 1; i++) {
    const a = {
      lon: path[i][0],
      lat: path[i][1]
    };
    const b = {
      lon: path[i + 1][0],
      lat: path[i + 1][1]
    };
    const d = pointToSegmentKm(click, a, b);
    if (d < best) best = d;
  }
  return best;
}
function pointToSegmentKm(p: {
  lat: number;
  lon: number;
}, a: {
  lat: number;
  lon: number;
}, b: {
  lat: number;
  lon: number;
}): number {
  // Local equirectangular projection (km) — accurate enough at NL scale
  // since segments are short. cos(lat) factor uses mean latitude.
  const meanLat = (a.lat + b.lat) / 2 * (Math.PI / 180);
  const kmPerDegLat = 111.32;
  const kmPerDegLon = 111.32 * Math.cos(meanLat);
  const ax = a.lon * kmPerDegLon;
  const ay = a.lat * kmPerDegLat;
  const bx = b.lon * kmPerDegLon;
  const by = b.lat * kmPerDegLat;
  const px = p.lon * kmPerDegLon;
  const py = p.lat * kmPerDegLat;
  const dx = bx - ax;
  const dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) {
    const ex = px - ax;
    const ey = py - ay;
    return Math.sqrt(ex * ex + ey * ey);
  }
  let t = ((px - ax) * dx + (py - ay) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  const cx = ax + t * dx;
  const cy = ay + t * dy;
  const ex = px - cx;
  const ey = py - cy;
  return Math.sqrt(ex * ex + ey * ey);
}
export const validateMapLocateLine: ValidatorFn = (userAnswer, question) => {
  const data = (question.data ?? {}) as Record<string, unknown>;
  const click = tryParsePoint(String(userAnswer ?? ''));
  const targetRiverId = String(data.targetRiverId ?? '');
  const tolKm = typeof data.toleranceKm === 'number' ? data.toleranceKm : 10;
  if (!click || !targetRiverId) {
    return {
      isCorrect: false
    };
  }
  const atlas = loadRivers();
  const target = atlas.rivers.find(r => r.id === targetRiverId);
  if (!target) {
    return {
      isCorrect: false
    };
  }
  const dist = pointToPolylineKm(click, target.path);
  const isCorrect = dist <= tolKm;
  return {
    isCorrect,
    correctAnswerDisplay: target.name
  };
};

// ── map_label_regions ────────────────────────────────────────

export const validateMapLabelRegions: ValidatorFn = (userAnswer, question) => {
  let placed: Record<string, string> = {};
  try {
    const parsed = JSON.parse(String(userAnswer ?? ''));
    if (parsed && typeof parsed === 'object') {
      placed = parsed as Record<string, string>;
    }
  } catch {
    return {
      isCorrect: false
    };
  }
  const data = (question.data ?? {}) as Record<string, unknown>;
  const labels = data.labels as Array<{
    id: string;
    correctRegionId: string;
  }> | undefined ?? [];
  if (labels.length === 0) return {
    isCorrect: false
  };
  const allCorrect = labels.every(l => placed[l.id] === l.correctRegionId);
  return {
    isCorrect: allCorrect
  };
};

// ── map_place_cities ─────────────────────────────────────────
// Place an ORDERED list of cities on the map. Payload is a JSON array
// of {lat, lon} matching the order of data.cityIds. Correct = ALL within tolerance.
// City lat/lon truths are resolved from the question's data.targetPoints array
// (so the validator does not depend on the cities atlas at runtime).

export const validateMapPlaceCities: ValidatorFn = (userAnswer, question) => {
  const data = (question.data ?? {}) as Record<string, unknown>;
  const targets = data.targetPoints as Array<{
    cityId?: string;
    name?: string;
    lat: number;
    lon: number;
  }> | undefined;
  const tolKm = typeof data.toleranceKm === 'number' ? data.toleranceKm : 35;
  if (!Array.isArray(targets) || targets.length === 0) {
    return {
      isCorrect: false
    };
  }
  let placements: Array<{
    lat: number;
    lon: number;
  } | null> = [];
  try {
    const parsed = JSON.parse(String(userAnswer ?? ''));
    if (Array.isArray(parsed)) placements = parsed;
  } catch {
    return {
      isCorrect: false
    };
  }
  if (placements.length !== targets.length) {
    return {
      isCorrect: false
    };
  }
  const perCity = targets.map((t, i) => {
    const p = placements[i];
    if (!p || typeof p.lat !== 'number' || typeof p.lon !== 'number' || !Number.isFinite(p.lat) || !Number.isFinite(p.lon)) {
      return {
        name: t.name ?? t.cityId ?? `Stad ${i + 1}`,
        ok: false
      };
    }
    const dist = haversineKm([p.lon, p.lat], [t.lon, t.lat]);
    return {
      name: t.name ?? t.cityId ?? `Stad ${i + 1}`,
      ok: dist <= tolKm
    };
  });
  const allCorrect = perCity.every(c => c.ok);

  // NOTE: do NOT set displayAnswer here — PracticeSession overwrites the raw
  // userTypedAnswer with displayAnswer, and the feedback layer
  // (formatMapPlaceCitiesUser + MapPlaceCitiesBreakdown) needs the raw JSON
  // array of placements to render the per-island breakdown with distance-off
  // labels. Display formatting happens in feedbackAnswerFormatters.ts from the
  // raw payload.
  return {
    isCorrect: allCorrect,
    correctAnswerDisplay: targets.map(t => t.name ?? t.cityId ?? '?').join(', ')
  };
};